import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetAllExternalApiKeysStatusQuery,
  useGetExternalApiKeyQuery,
  useUpdateExternalApiKeyMutation,
  ExternalServiceName,
} from '../../store/api/adminApi';

export function AdminExternalApiKeysPage() {
  const { t } = useTranslation();
  const [editingService, setEditingService] = useState<ExternalServiceName | null>(null);
  const [formData, setFormData] = useState<{
    apiKey: string;
    apiSecret: string;
    username: string;
  }>({
    apiKey: '',
    apiSecret: '',
    username: '',
  });

  const { data: statusData, isLoading: statusLoading } = useGetAllExternalApiKeysStatusQuery();
  const { data: keyData, isLoading: keyLoading } = useGetExternalApiKeyQuery(
    editingService!,
    { skip: !editingService }
  );

  const [updateExternalApiKey, { isLoading: isUpdating }] = useUpdateExternalApiKeyMutation();

  const getServiceDisplayName = (serviceName: ExternalServiceName): string => {
    const names: Record<ExternalServiceName, string> = {
      smspool: 'SMSPool',
      pvadeals: 'PVA Deals',
      durianRcs: 'DurianRCS',
      proxylte: 'ProxyLTE',
      proxyline: 'ProxyLine',
      proxycheap: 'ProxyCheap',
      proxy5g: '5G Proxy',
      cryptomus: 'Cryptomus',
    };
    return names[serviceName] || serviceName;
  };

  const handleStartEdit = (serviceName: ExternalServiceName) => {
    setEditingService(serviceName);
    // سيتم ملء البيانات من useGetExternalApiKeyQuery
  };

  // تحديث formData عند جلب البيانات
  useEffect(() => {
    if (keyData?.data.keyConfig && editingService) {
      setFormData({
        apiKey: keyData.data.keyConfig.apiKey || '',
        apiSecret: keyData.data.keyConfig.apiSecret || '',
        username: keyData.data.keyConfig.username || '',
      });
    }
  }, [keyData, editingService]);

  const handleUpdate = async () => {
    if (!editingService) return;

    // التحقق من أن هناك على الأقل حقل واحد
    if (!formData.apiKey && !formData.apiSecret && !formData.username) {
      toast.error(t('admin.externalApiKeys.validation.atLeastOneField'));
      return;
    }

    try {
      const updateData: {
        apiKey?: string | null;
        apiSecret?: string | null;
        username?: string | null;
      } = {};

      if (formData.apiKey !== undefined) {
        updateData.apiKey = formData.apiKey || null;
      }
      if (formData.apiSecret !== undefined) {
        updateData.apiSecret = formData.apiSecret || null;
      }
      if (formData.username !== undefined) {
        updateData.username = formData.username || null;
      }

      await updateExternalApiKey({
        serviceName: editingService,
        data: updateData,
      }).unwrap();

      toast.success(t('admin.externalApiKeys.updateSuccess'));
      setEditingService(null);
      setFormData({ apiKey: '', apiSecret: '', username: '' });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.externalApiKeys.updateError')
      );
    }
  };

  const handleCancel = () => {
    setEditingService(null);
    setFormData({ apiKey: '', apiSecret: '', username: '' });
  };

  if (statusLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const keysStatus = statusData?.data.keysStatus || ({} as Record<ExternalServiceName, { apiKeyConfigured: boolean; apiSecretConfigured: boolean; usernameConfigured: boolean; hasApiSecret: boolean; hasUsername: boolean }>);

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.externalApiKeys.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.externalApiKeys.subtitle')}
        </p>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(Object.keys(keysStatus) as ExternalServiceName[]).map((serviceName) => {
          const status = keysStatus[serviceName];
          const isEditing = editingService === serviceName;
          const isLoadingKey = isEditing && keyLoading;

          return (
            <div
              key={serviceName}
              className="card-glass rounded-2xl shadow-2xl p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-heading font-bold text-brand-accent">
                  {getServiceDisplayName(serviceName)}
                </h3>
                {!isEditing && (
                  <button
                    onClick={() => handleStartEdit(serviceName)}
                    className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                  >
                    {t('admin.externalApiKeys.edit')}
                  </button>
                )}
              </div>

              {!isEditing ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-medium font-body">
                      {t('admin.externalApiKeys.apiKey')}:
                    </span>
                    <span
                      className={`text-sm font-heading font-semibold ${
                        status.apiKeyConfigured ? 'text-green-500' : 'text-neutral-medium'
                      }`}
                    >
                      {status.apiKeyConfigured
                        ? t('admin.externalApiKeys.configured')
                        : t('admin.externalApiKeys.notConfigured')}
                    </span>
                  </div>
                  {status.hasApiSecret && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-medium font-body">
                        {t('admin.externalApiKeys.apiSecret')}:
                      </span>
                      <span
                        className={`text-sm font-heading font-semibold ${
                          status.apiSecretConfigured ? 'text-green-500' : 'text-neutral-medium'
                        }`}
                      >
                        {status.apiSecretConfigured
                          ? t('admin.externalApiKeys.configured')
                          : t('admin.externalApiKeys.notConfigured')}
                      </span>
                    </div>
                  )}
                  {status.hasUsername && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-medium font-body">
                        {t('admin.externalApiKeys.username')}:
                      </span>
                      <span
                        className={`text-sm font-heading font-semibold ${
                          status.usernameConfigured ? 'text-green-500' : 'text-neutral-medium'
                        }`}
                      >
                        {status.usernameConfigured
                          ? t('admin.externalApiKeys.configured')
                          : t('admin.externalApiKeys.notConfigured')}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {isLoadingKey ? (
                    <div className="text-center py-4">
                      <p className="text-neutral-medium text-sm font-body animate-pulse">
                        {t('common.loading')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* API Key */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('admin.externalApiKeys.apiKey')}
                        </label>
                        <input
                          type="text"
                          value={formData.apiKey}
                          onChange={(e) =>
                            setFormData({ ...formData, apiKey: e.target.value })
                          }
                          placeholder={t('admin.externalApiKeys.apiKeyPlaceholder')}
                          className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                        />
                      </div>

                      {/* API Secret (if applicable) */}
                      {status.hasApiSecret && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                            {t('admin.externalApiKeys.apiSecret')}
                          </label>
                          <input
                            type="text"
                            value={formData.apiSecret}
                            onChange={(e) =>
                              setFormData({ ...formData, apiSecret: e.target.value })
                            }
                            placeholder={t('admin.externalApiKeys.apiSecretPlaceholder')}
                            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                          />
                        </div>
                      )}

                      {/* Username (if applicable) */}
                      {status.hasUsername && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                            {t('admin.externalApiKeys.username')}
                          </label>
                          <input
                            type="text"
                            value={formData.username}
                            onChange={(e) =>
                              setFormData({ ...formData, username: e.target.value })
                            }
                            placeholder={t('admin.externalApiKeys.usernamePlaceholder')}
                            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                          />
                        </div>
                      )}

                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={handleUpdate}
                          disabled={isUpdating}
                          className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
                        >
                          {isUpdating ? (
                            <>
                              <ButtonSpinner size="xs" />
                              <span>{t('common.loading')}</span>
                            </>
                          ) : (
                            t('common.save')
                          )}
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 text-sm"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

